import { useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { supabase } from '@/lib/supabase'
import { Dialog } from '@/shared/components/Dialog'
import { useDeletionPreview } from './useDeletionPreview'
import { useDeleteAccount } from './useDeleteAccount'

interface Props {
  open: boolean
  onClose: () => void
}

export function DeleteAccountDialog({ open, onClose }: Props) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [confirmText, setConfirmText] = useState('')
  const { data: preview, isLoading } = useDeletionPreview(open)
  const deleteAccount = useDeleteAccount()

  const isConfirmed = confirmText === 'DELETE'

  const handleDelete = () => {
    deleteAccount.mutate(undefined, {
      onSuccess: async () => {
        await supabase.auth.signOut()
        navigate({ to: '/login' })
      },
    })
  }

  return (
    <Dialog open={open} onClose={onClose} title={t('account.delete.dialogTitle')}>
      {isLoading && <p className="text-sm text-muted py-2">{t('common.loading')}</p>}

      {preview && (
        <div className="space-y-2 text-sm text-muted">
          {preview.willDeleteHousehold && preview.householdName && (
            <p>{t('account.delete.willDeleteHousehold', { name: preview.householdName })}</p>
          )}
          {!preview.willDeleteHousehold && preview.memberCount > 1 && (
            <p>{t('account.delete.willPreserveData', { count: preview.memberCount - 1 })}</p>
          )}
          {preview.willPromoteMember && (
            <p>{t('account.delete.willPromoteMember')}</p>
          )}
          <p>{t('account.delete.piiRemoval')}</p>
          <p className="font-semibold text-ink">{t('account.delete.irreversible')}</p>
        </div>
      )}

      <div className="space-y-1 mt-4">
        <label htmlFor="confirm-delete" className="block text-sm font-medium text-muted">
          {t('account.delete.confirmPrompt')}
        </label>
        <input
          id="confirm-delete"
          type="text"
          placeholder={t('account.delete.confirmPlaceholder')}
          value={confirmText}
          onChange={(e) => setConfirmText(e.target.value)}
          className="w-full rounded-lg border border-line bg-canvas px-3.5 py-2.5 text-ink placeholder:text-faint focus:outline-none focus:ring-2 focus:ring-danger focus:border-transparent transition"
        />
      </div>

      <div className="flex gap-3 mt-6">
        <button
          onClick={handleDelete}
          disabled={!isConfirmed || deleteAccount.isPending}
          className="flex-1 rounded-lg bg-danger text-white font-semibold py-2.5 px-4 hover:bg-danger/90 focus:outline-none focus:ring-2 focus:ring-danger transition disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {t('account.delete.submitButton')}
        </button>
        <button
          onClick={onClose}
          className="flex-1 rounded-lg border border-line bg-canvas text-ink font-medium py-2.5 px-4 hover:bg-surface focus:outline-none focus:ring-2 focus:ring-accent transition"
        >
          {t('common.cancel')}
        </button>
      </div>
    </Dialog>
  )
}
